import { getAccessToken } from './getAccessToken';
// @ts-ignore
import moment from 'moment';

// const environmentUrl = "https://org487f0635.crm8.dynamics.com";dev
const environmentUrl = "https://orgea61b289.crm8.dynamics.com";

// Interface for Quality Wall Record data
export interface QualityWallRecordData {
  cr3ea_qualitytourid: string;
  cr3ea_title: string;
  cr3ea_cycle: string;
  cr3ea_shift: string;
  cr3ea_observedby: string | null;
  cr3ea_productname: string;
  cr3ea_batchno: string;
  cr3ea_coding: string;
  cr3ea_lineno: string;
  cr3ea_memberspresented: string;
  cr3ea_packappearance: string;
  cr3ea_sealingquality: string;
  cr3ea_executivename: string;
  cr3ea_category: string;
  cr3ea_sku: string;
  cr3ea_rating?: string | null;
  cr3ea_platform?: string;
  // Product Quality fields
  cr3ea_appearance?: string | null;
  cr3ea_color?: string | null;
  cr3ea_texture?: string | null;
  cr3ea_flavour?: string | null;
  cr3ea_taste?: string | null;
  cr3ea_remarksdefect?: string | null;
}

// Interface for evaluation entry (supports both Pack Quality and Product Quality)
export interface EvaluationEntry {
  membersPresent: string;
  packAppearance: string;
  sealingQuality: string;
  coding: string;
  rating?: string;
  // Product Quality fields
  appearance?: string;
  color?: string;
  texture?: string;
  flavour?: string;
  taste?: string;
  remarksDefect?: string;
  // Per-row category so Pack/Product entries can coexist for same cycle
  category?: string;
}

// Interface for start form data
export interface StartFormData {
  productName: string;
  batchNo: string;
  category: string;
  lineNo: string;
  executiveName: string;
  sku: string;
  rating?: string;
}

// Interface for save response
export interface SaveResponse {
  success: boolean;
  message: string;
  data?: any;
}

// Interface for cycle data response
export interface CycleDataResponse {
  cycleNum: string;
  batchNum: string;
  product: string;
  executiveName: string;
  lineNum: string;
  category: string;
  sku: string;
  rowsSummary: EvaluationEntry[];
}

/**
 * Collect estimation data for cycle save
 * Converts component state to API format
 */
export async function collectEstimationDataCycleSave(
  cycleNum: number,
  startFormData: StartFormData,
  evaluationEntries: EvaluationEntry[],
  qualityTourId: string,
  userName: string
): Promise<{ savedData: QualityWallRecordData[]; summary: any }> {
  try {
    console.log('=== COLLECTING QUALITY WALL RECORD DATA FOR CYCLE SAVE ===');
    console.log('Cycle Number:', cycleNum);
    console.log('Start Form Data:', startFormData);
    console.log('Evaluation Entries:', evaluationEntries);
    console.log('Quality Tour ID:', qualityTourId);
    console.log('User Name:', userName);

    const product = startFormData.productName || "N/A";
    const executiveName = startFormData.executiveName || "N/A";
    const lineNum = startFormData.lineNo || "N/A";
    const batchNum = startFormData.batchNo || "N/A";
    const category = startFormData.category || "";
    const sku = startFormData.sku || "";

    const recordsToSave: QualityWallRecordData[] = [];
    const rowsSummary: EvaluationEntry[] = [];

    const isProductQuality = category === 'Product Quality';

    // Process each evaluation entry
    evaluationEntries.forEach((entry) => {
      const members = (entry.membersPresent || '').trim();
      const pack = (entry.packAppearance || '').trim();
      const sealing = (entry.sealingQuality || '').trim();
      const codingVal = (entry.coding || '').trim();
      const rating = (entry.rating || '').trim() || null;
      const appearance = (entry.appearance || '').trim();
      const color = (entry.color || '').trim();
      const texture = (entry.texture || '').trim();
      const flavour = (entry.flavour || '').trim();
      const taste = (entry.taste || '').trim();
      const remarksDefect = (entry.remarksDefect || '').trim();

      if (isProductQuality) {
        // Skip empty Product Quality entries
        if (!members && !appearance && !color && !texture && !flavour && !taste && !remarksDefect) {
          return;
        }
      } else {
        // Skip empty Pack Quality entries
        if (!members && !pack && !sealing && !codingVal) {
          return;
        }
      }

      const record: QualityWallRecordData = {
        cr3ea_qualitytourid: qualityTourId,
        cr3ea_title: 'Qualitywall_' + moment().format('DD/MM/YYYY'),
        cr3ea_cycle: `Cycle-${cycleNum}`,
        cr3ea_shift: sessionStorage.getItem("shiftValue") || "shift 1",
        cr3ea_observedby: userName || null,
        cr3ea_productname: product,
        cr3ea_batchno: batchNum,
        cr3ea_coding: isProductQuality ? '' : codingVal,
        cr3ea_lineno: lineNum,
        cr3ea_memberspresented: members,
        cr3ea_packappearance: isProductQuality ? '' : pack,
        cr3ea_sealingquality: isProductQuality ? '' : sealing,
        cr3ea_executivename: executiveName,
        cr3ea_category: category,
        cr3ea_sku: sku,
        cr3ea_rating: rating,
        cr3ea_platform: "PWA"
      } as QualityWallRecordData;

      if (isProductQuality) {
        (record as any).cr3ea_appearance = appearance || null;
        (record as any).cr3ea_color = color || null;
        (record as any).cr3ea_texture = texture || null;
        (record as any).cr3ea_flavour = flavour || null;
        (record as any).cr3ea_taste = taste || null;
        (record as any).cr3ea_remarksdefect = remarksDefect || null;
      }

      recordsToSave.push(record);

      rowsSummary.push({
        membersPresent: members,
        packAppearance: pack,
        sealingQuality: sealing,
        coding: codingVal,
        rating: rating || undefined,
        appearance,
        color,
        texture,
        flavour,
        taste,
        remarksDefect,
        category
      });
    });

    if (recordsToSave.length === 0) {
      throw new Error("Please enter at least one row of data.");
    }

    console.log('Records to save:', recordsToSave.length);
    console.log('Records data:', recordsToSave);

    return {
      savedData: recordsToSave,
      summary: {
        cycleNum,
        rowsSummary,
        product,
        lineNum,
        batchNum,
        executiveName,
        category,
        sku
      }
    };
  } catch (error) {
    console.error('Error collecting estimation data:', error);
    throw error;
  }
}

/**
 * Save section API call
 * Saves records to cr3ea_prod_qualitywalls table
 */
export async function saveSectionApiCall(data: QualityWallRecordData[]): Promise<SaveResponse> {
  try {
    console.log('=== SAVING QUALITY WALL RECORD DATA ===');
    console.log('Data to save:', data);
    console.log('Number of records:', data.length);

    const tokenResult = await getAccessToken();
    const accessToken = tokenResult?.token;
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "Content-Type": "application/json; charset=utf-8",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Prefer": "return=representation",
      "Authorization": `Bearer ${accessToken}`
    };

    const apiVersion = "9.2";
    const tableName = "cr3ea_prod_qualitywalls";
    const apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}`;

    const results = [];
    for (const record of data) {
      console.log('Saving record:', record);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(record)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to save record:', response.status, errorText);
        throw new Error(`Failed to save record: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('Record saved successfully:', result);
      results.push(result);
    }

    console.log(`${data.length} records saved successfully`);
    return {
      success: true,
      message: `Successfully saved ${data.length} record(s)`,
      data: results
    };
  } catch (error) {
    console.error('Error saving records:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to save records'
    };
  }
}

/**
 * Fetch cycle data from API
 * Retrieves all quality wall records for a given quality tour ID
 */
export async function fetchCycleData(qualityTourId: string): Promise<CycleDataResponse[]> {
  try {
    console.log('=== FETCHING QUALITY WALL RECORD CYCLE DATA ===');
    console.log('Quality Tour ID:', qualityTourId);

    const tokenResult = await getAccessToken();
    const accessToken = tokenResult?.token;
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Authorization": `Bearer ${accessToken}`
    };

    const apiVersion = "9.2";
    const tableName = "cr3ea_prod_qualitywalls";
    const select = [
      'cr3ea_cycle',
      'cr3ea_batchno',
      'cr3ea_productname',
      'cr3ea_coding',
      'cr3ea_lineno',
      'cr3ea_memberspresented',
      'cr3ea_packappearance',
      'cr3ea_executivename',
      'cr3ea_sealingquality',
      'cr3ea_shift',
      'cr3ea_sku',
      'cr3ea_category',
      'cr3ea_rating',
      'cr3ea_appearance',
      'cr3ea_color',
      'cr3ea_texture',
      'cr3ea_flavour',
      'cr3ea_taste',
      'cr3ea_remarksdefect'
    ].join(',');
    
    const apiUrl =
      `${environmentUrl}/api/data/v${apiVersion}/${tableName}?` +
      `$filter=cr3ea_qualitytourid eq '${qualityTourId}'` +
      `&$select=${select}`;

    console.log('Fetching from URL:', apiUrl);

    const response = await fetch(apiUrl, { headers });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch data:', response.status, errorText);
      throw new Error(`Failed to fetch data: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('API Response:', data);
    console.log('Number of records:', data.value?.length || 0);

    if (!data.value || !Array.isArray(data.value)) {
      console.warn('Invalid response format, data.value is not an array');
      return [];
    }

    const cycles: { [key: string]: CycleDataResponse } = {};

    data.value.forEach((record: any) => {
      const cycleNum = (record.cr3ea_cycle || '').replace('Cycle-', '');
      if (!cycleNum) {
        console.warn('Skipping record with invalid cycle:', record.cr3ea_cycle);
        return;
      }

      if (!cycles[cycleNum]) {
        cycles[cycleNum] = {
          cycleNum,
          batchNum: record.cr3ea_batchno || "N/A",
          product: record?.cr3ea_productname || "N/A",
          executiveName: record?.cr3ea_executivename || "N/A",
          lineNum: record?.cr3ea_lineno || "N/A",
          category: record?.cr3ea_category || "",
          sku: record?.cr3ea_sku || "",
          rowsSummary: []
        };
      }

      cycles[cycleNum].rowsSummary.push({
        membersPresent: record?.cr3ea_memberspresented || "",
        packAppearance: record?.cr3ea_packappearance || "",
        sealingQuality: record?.cr3ea_sealingquality || "",
        coding: record?.cr3ea_coding || "",
        rating: record?.cr3ea_rating ?? undefined,
        appearance: record?.cr3ea_appearance || "",
        color: record?.cr3ea_color || "",
        texture: record?.cr3ea_texture || "",
        flavour: record?.cr3ea_flavour || "",
        taste: record?.cr3ea_taste || "",
        remarksDefect: record?.cr3ea_remarksdefect || "",
        category: record?.cr3ea_category || ""
      });
    });

    const result = Object.values(cycles);
    console.log('Processed cycles:', result.length);
    return result;
  } catch (error) {
    console.error('Error fetching cycle data:', error);
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
    return [];
  }
}

// Interface for offline submission
export interface QualityWallRecordOfflineSubmission {
  cycleNo: number;
  records: QualityWallRecordData[];
  timestamp: number;
  plantTourId: string;
}

// Interface for sync response
export interface SyncOfflineDataResponse {
  success: boolean;
  message: string;
  syncedCount?: number;
  errorCount?: number;
}

/**
 * Sync offline data to backend
 * Takes offline submissions and saves them to the API
 */
export async function syncOfflineDataToBackend(
  offlineSubmissions: QualityWallRecordOfflineSubmission[],
  qualityTourId: string,
  userName: string,
  selectedShift?: string | null
): Promise<SyncOfflineDataResponse> {
  try {
    console.log('=== SYNCING QUALITY WALL RECORD OFFLINE DATA TO BACKEND ===');
    console.log('Offline submissions to sync:', offlineSubmissions);
    console.log('Offline submissions length:', offlineSubmissions.length);
    console.log('Quality Tour ID:', qualityTourId);
    console.log('User Name:', userName);
    console.log('Selected Shift:', selectedShift);

    if (!offlineSubmissions || offlineSubmissions.length === 0) {
      console.log('No offline data to sync');
      return {
        success: true,
        message: 'No offline data to sync',
        syncedCount: 0,
        errorCount: 0
      };
    }

    const tokenResult = await getAccessToken();
    const accessToken = tokenResult?.token;
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    let totalSynced = 0;
    let totalErrors = 0;
    const errors: string[] = [];

    for (const submission of offlineSubmissions) {
      try {
        const cycleNo = submission.cycleNo;
        const records = submission.records || [];

        console.log(`Processing offline data for cycle ${cycleNo}:`, records);

        // Ensure platform is set and quality tour ID is correct
        const recordsToSave = records.map((record: any) => ({
          ...record,
          cr3ea_platform: 'PWA', // Ensure platform is set
          cr3ea_qualitytourid: qualityTourId || submission.plantTourId || record.cr3ea_qualitytourid,
          cr3ea_shift: selectedShift || record.cr3ea_shift || sessionStorage.getItem("shiftValue") || "shift 1"
        }));

        // Save records to API
        const saveResult = await saveSectionApiCall(recordsToSave);

        if (saveResult.success) {
          console.log(`Successfully synced cycle ${cycleNo}`);
          totalSynced += recordsToSave.length;
        } else {
          throw new Error(`Failed to save cycle ${cycleNo}: ${saveResult.message}`);
        }
      } catch (error) {
        console.error(`Failed to sync cycle ${submission.cycleNo}:`, error);
        errors.push(`Cycle ${submission.cycleNo}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        totalErrors++;
      }
    }

    const message = `Synced ${totalSynced} record(s) successfully${totalErrors > 0 ? `, ${totalErrors} error(s)` : ''}`;
    console.log('Sync completed:', message);

    return {
      success: totalErrors === 0,
      message,
      syncedCount: totalSynced,
      errorCount: totalErrors
    };
  } catch (error) {
    console.error('Error syncing offline data:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to sync offline data',
      syncedCount: 0,
      errorCount: offlineSubmissions.length
    };
  }
}

